"use client";

import { useState, useEffect, useMemo } from "react";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";

import { useStickyContentContext } from "../StickyContentContext";
import { VoiceChangerMockDashboard } from "@ui/components/MockDashboards";

// Responsive pan offsets in rem, ordered from largest breakpoint to smallest
const RESPONSIVE_PAN_OFFSETS = [
  { query: "(min-width: 80rem)", rem: 27.5 }, // xl: panel 22.5rem + 5rem buffer
  { query: "(min-width: 64rem)", rem: 25 }, // lg: panel 22.5rem + 2.5rem buffer
  { query: "(min-width: 48rem)", rem: 22.5 }, // md: panel 20rem + 2.5rem buffer
  { query: "(min-width: 40rem)", rem: 22 }, // sm: panel 17.5rem + 4.5rem extra (partially hides disc)
] as const;

const BASE_PAN_OFFSET_REM = 20; // panel 15rem + 5rem extra (partially hides disc)
const CARD_SHADOW = "0px 2px 4px 0px rgba(0, 0, 0, 0.078), 0px 0px 0px 0.9px rgba(0, 0, 0, 0.071)";

// Hook to get responsive pan offset in rem based on current viewport
function usePanOffsetRem() {
  const [panOffset, setPanOffset] = useState<number>(BASE_PAN_OFFSET_REM);

  useEffect(() => {
    const mediaQueries = RESPONSIVE_PAN_OFFSETS.map(bp => ({
      rem: bp.rem,
      mql: window.matchMedia(bp.query),
    }));

    const updatePanOffset = () => {
      const match = mediaQueries.find(({ mql }) => mql.matches);
      setPanOffset(match?.rem ?? BASE_PAN_OFFSET_REM);
    };

    updatePanOffset();

    mediaQueries.forEach(({ mql }) => mql.addEventListener("change", updatePanOffset));
    return () => {
      mediaQueries.forEach(({ mql }) => mql.removeEventListener("change", updatePanOffset));
    };
  }, []);

  return panOffset;
}

// Component names that form a pan pair
const PAN_PAIR_COMPONENTS = ["voiceChangerPanLeft", "voiceChangerPanRight"] as const;

interface VoiceChangerPanDemoProps {
  defaultView: "left" | "right";
}

export function VoiceChangerPanDemo({ defaultView }: VoiceChangerPanDemoProps) {
  const { activeIndex, items } = useStickyContentContext();
  const panOffsetRem = usePanOffsetRem();

  // Find indices of pan pair components in the items array
  const panPairIndices = useMemo(
    () =>
      items
        .map((item, index) => {
          const template = item.stickyContent?.template === "component" ? item.stickyContent.component?.template : null;
          if (template && PAN_PAIR_COMPONENTS.includes(template as (typeof PAN_PAIR_COMPONENTS)[number])) {
            return { index, template };
          }
          return null;
        })
        .filter(Boolean) as { index: number; template: string }[],
    [items],
  );

  // Check if we have a partner (both left and right exist)
  const hasPartner = panPairIndices.length >= 2;

  // Determine pan offset based on active index
  let panOffset = 0;

  if (hasPartner) {
    const rightIndex = panPairIndices.find(p => p.template === "voiceChangerPanRight")?.index ?? -1;

    if (activeIndex >= rightIndex && rightIndex !== -1) {
      // Pan to show voice selection (shift left)
      panOffset = -panOffsetRem;
    }
    // Otherwise panOffset stays at 0 (show sidebar view)
  } else {
    // No partner - show static view based on defaultView
    panOffset = defaultView === "right" ? -panOffsetRem : 0;
  }

  // Initial position should be the opposite of target so animation happens on mount
  // Left component enters from the panned state (coming back from right)
  // Right component enters from the unpanned state (coming from left)
  const initialOffset = defaultView === "left" ? -panOffsetRem : 0;

  const mobileOffsetRem = defaultView === "right" ? -panOffsetRem : 0;

  const t = useTranslations("ClientKeys.BlockStickyContentList.VoiceChangerPanDemo");

  return (
    <div
      className='tw-h-full tw-pt-2 tw-px-2 sm:tw-pt-3 sm:tw-px-3 md:tw-pt-4 md:tw-px-4 tw-relative tw-overflow-hidden'
      role='img'
      aria-label={t("aria_label")}
      inert
    >
      <div
        className='tw-h-full tw-bg-white tw-rounded-t-xl sm:tw-rounded-t-2xl lg:tw-hidden'
        style={{
          width: `calc(100% + ${panOffsetRem}rem)`,
          transform: `translateX(${mobileOffsetRem}rem)`,
          boxShadow: CARD_SHADOW,
        }}
      >
        <VoiceChangerMockDashboard />
      </div>
      <m.div
        className='tw-hidden lg:tw-block tw-h-full tw-bg-white tw-rounded-t-2xl'
        initial={{ x: `${initialOffset}rem` }}
        animate={{ x: `${panOffset}rem` }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 50,
        }}
        style={{
          width: `calc(100% + ${panOffsetRem}rem)`,
          boxShadow: CARD_SHADOW,
        }}
      >
        <VoiceChangerMockDashboard />
      </m.div>
      <div className='tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-[30%] tw-bg-gradient-to-t tw-from-[#FDFCFC] tw-to-transparent tw-pointer-events-none' />
    </div>
  );
}
